import { useState, useEffect, useCallback } from 'react';
import { CreditCard, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageHeader, StatCard, API } from './_P1Common';
import { useAdmin } from '@/hooks/useAdmin';

export default function AdminPayments() {
  const { authFetch } = useAdmin();
  const [data, setData] = useState(null);
  const [processors, setProcessors] = useState([]);
  const [provider, setProvider] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, procRes] = await Promise.all([
        authFetch(`${API}/admin/payments?provider=${encodeURIComponent(provider)}`).then(r => r.json()),
        authFetch(`${API}/admin/payment-processors`).then(r => r.json()),
      ]);
      setData(pRes);
      setProcessors(procRes.processors || []);
    } finally { setLoading(false); }
  }, [authFetch, provider]);

  useEffect(() => { load(); }, [load]);

  const toggleProcessor = async (key, enabled) => {
    const r = await authFetch(`${API}/admin/payment-processors`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, enabled }),
    });
    if (r.ok) {
      toast.success(`${key.toUpperCase()} ${enabled ? 'uključen' : 'isključen'}`);
      setProcessors(prev => prev.map(p => p.key === key ? { ...p, enabled } : p));
    } else toast.error('Greška pri promjeni stanja');
  };

  return (
    <div data-testid="admin-payments">
      <AdminPageHeader icon={CreditCard} title="Plaćanja" subtitle="Stripe · PayPal · MyPOS">
        <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="payments-filter"
                className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
          <option value="">Svi pružatelji</option>
          <option value="stripe">Stripe</option>
          <option value="paypal">PayPal</option>
          <option value="mypos">MyPOS</option>
        </select>
      </AdminPageHeader>

      {/* Processor toggles */}
      <div className="px-4 md:px-6 mb-6" data-testid="processor-toggles">
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-300 mb-3">Dostupni pružatelji plaćanja</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {processors.map(p => (
            <div key={p.key}
                 className={`rounded-lg border p-4 flex items-center justify-between ${p.enabled ? 'border-emerald-600 bg-emerald-600/5' : 'border-zinc-800 bg-zinc-900/60'}`}
                 data-testid={`processor-${p.key}`}>
              <div>
                <p className="text-sm font-bold text-white">{p.label}</p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  {p.enabled ? 'Aktivan — korisnici mogu plaćati' : 'Onemogućen — gumb plaćanja je skriven'}
                </p>
              </div>
              <button onClick={() => toggleProcessor(p.key, !p.enabled)}
                      data-testid={`processor-toggle-${p.key}`}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${p.enabled ? 'bg-emerald-600' : 'bg-zinc-700'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${p.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                {p.enabled
                  ? <Check size={10} className="absolute left-1.5 text-white" />
                  : <X size={10} className="absolute right-1.5 text-zinc-400" />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {data && (
        <div className="px-4 md:px-6 grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard testid="pay-total" label="Ukupno" value={data.totals.total} accent="blue"/>
          <StatCard testid="pay-stripe" label="Stripe" value={data.totals.stripe}/>
          <StatCard testid="pay-paypal" label="PayPal" value={data.totals.paypal} accent="violet"/>
          <StatCard testid="pay-mypos" label="MyPOS" value={data.totals.mypos} accent="green"/>
          <StatCard testid="pay-refunded" label="Povrati" value={data.totals.refunded} accent="amber"/>
        </div>
      )}
      <div className="px-4 md:px-6 pb-6 overflow-x-auto rounded-lg border border-zinc-800 mx-4 md:mx-6">
        {loading ? <Loader2 className="animate-spin m-4"/> : (
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900 text-xs text-zinc-400 uppercase">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Pružatelj</th>
                <th className="px-4 py-2 text-left">Iznos</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Kreirano</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map(p => (
                <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-900/50">
                  <td className="px-4 py-2 text-xs text-zinc-500">{p.id?.slice(0, 8)}</td>
                  <td className="px-4 py-2">{p.provider || '—'}</td>
                  <td className="px-4 py-2">{p.amount_eur ? `€${p.amount_eur}` : '—'}</td>
                  <td className="px-4 py-2">{p.status || '—'}</td>
                  <td className="px-4 py-2 text-xs text-zinc-500">{(p.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
              {(data?.items || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">Nema plaćanja.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
